import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { SectionPlaceholder } from '../../../../components/SectionPlaceholder/SectionPlaceholder';
import { useShowSummaryV2 } from '../useShowSummaryV2';
import { SummarySection } from '../SummarySection';
import { AsOfDatePicker } from './AsOfDatePicker';
import { HoldingsGrid } from './HoldingsGrid';
import { HoldingsKpiCards } from './HoldingsKpiCards';
import { OwnershipTimelineChart } from './OwnershipTimelineChart';
import { selectedRoundJ } from './state';
import { ViewModeToggle } from './ViewModeToggle';

const headerRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0 0.5rem 0.5rem;
`;

const bodyPlaceholder = css`
  margin: 0.25rem 0.5rem;
`;

/** The design nests the timeline in its own tinted sub-card inside the section. */
const timelineCard = css`
  background-color: var(--mui-palette-action-hover);
  border-radius: 4px;
  margin: 0 0.5rem 0.5rem;
  padding: 0.75rem 0.25rem 0.25rem;
`;

const timelineHeader = css`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0 0.5rem 0.5rem;
`;

const timelineTitle = css`
  font-weight: 500;
`;

const timelineSpacer = css`
  flex: 1;
`;

interface IOwnershipTimelinePanelProps {
  companyId: number;
}

export function OwnershipTimelinePanel({ companyId }: IOwnershipTimelinePanelProps) {
  return (
    <SummarySection title='Equity & Ownership Holdings'>
      <Suspense fallback={<Typography sx={{ p: 1 }}>Loading ownership timeline…</Typography>}>
        <PanelBody companyId={companyId} />
      </Suspense>
    </SummarySection>
  );
}

function PanelBody({ companyId }: IOwnershipTimelinePanelProps) {
  const showSummaryV2 = useShowSummaryV2();

  // Per-section Suspense gates the initial fetch granularly. Date changes no longer re-suspend —
  // holdings filter in-memory and the chart's data is date-independent.
  const kpis = (
    <Suspense fallback={<SectionPlaceholder css={bodyPlaceholder} label='Loading metrics…' />}>
      <HoldingsKpiCards companyId={companyId} />
    </Suspense>
  );
  const chart = (
    <Suspense fallback={<SectionPlaceholder css={bodyPlaceholder} label='Loading chart…' />}>
      <OwnershipTimelineChart companyId={companyId} />
    </Suspense>
  );
  const holdings = (
    <Suspense fallback={<SectionPlaceholder css={bodyPlaceholder} label='Loading holdings…' />}>
      <MyHoldingsSection companyId={companyId} />
    </Suspense>
  );

  if (!showSummaryV2) {
    return (
      <>
        <div css={headerRow}>
          <AsOfDatePicker companyId={companyId} />
          <ViewModeToggle companyId={companyId} />
        </div>
        {kpis}
        {chart}
        {holdings}
      </>
    );
  }

  // Per design the metrics come first, then a titled "Ownership Timeline" sub-card that owns the
  // date picker, the view toggle and the chart, then My Holdings.
  return (
    <>
      {kpis}
      <section css={timelineCard}>
        <div css={timelineHeader}>
          <Typography variant='subtitle1' css={timelineTitle}>
            Ownership Timeline
          </Typography>
          <AsOfDatePicker companyId={companyId} />
          <div css={timelineSpacer} />
          <ViewModeToggle companyId={companyId} />
        </div>
        {chart}
      </section>
      {holdings}
    </>
  );
}

const holdingsHeader = css`
  padding: 0.5rem 0.5rem 0.25rem;
`;

function MyHoldingsSection({ companyId }: IOwnershipTimelinePanelProps) {
  const showSummaryV2 = useShowSummaryV2();
  const selected = useAtomValue(selectedRoundJ(companyId));

  // The design labels this plainly; V1 appends the selected round for context.
  const heading = showSummaryV2 ? 'My Holdings' : `My Holdings${selected ? ` · ${selected.roundLabel}` : ''}`;

  return (
    <div>
      <Typography variant='subtitle2' css={holdingsHeader}>
        {heading}
      </Typography>
      <HoldingsGrid companyId={companyId} />
    </div>
  );
}
